// @ts-strict-ignore
import { UserModel } from '@user/model/user.model';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Dictionary } from 'typescript-collections';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ClipboardManager } from '@project/components/article/manager/clipboard.manager';
import { Vector3 } from 'three';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { ProjectModel } from '@project/model/project.model';
import { CopiedRangeOnLineArticleInformation } from '@project/components/article/model/copied-range-on-line-article-information';
import { CopiedArticleInformation } from '@project/components/article/model/copied-article-information';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { SelectionModel } from '@project/components/article/model/selection.model';
import { ProjectType } from '../../../model/project-type';
import { IsProjectType } from '../../../model/is-project-type';
import { ArticleManager } from '@article/manager/article.manager';
import { OverlapInfo } from '@3d-scene/model/overlap-info';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { PasteArticleInformation } from '@project/components/article/model/paste-article-information';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { PasteArticleProcessor } from '@project/components/article/processor/paste-article.processor';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { UndoAttachArticlesOperation } from '@shared/undo/operation/undo-attach-articles-operation';
import { UndoInsertArticlesByLocationOperation } from '@shared/undo/operation/undo-insert-articles-by-location-operation';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { CarrierBlockManager } from '@project/components/article/manager/carrier-block.manager';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ToastrService } from 'ngx-toastr';
import { ReplaceLabelingUtils } from '../../label/replace-labeling-helper/replace-labeling-utils';
import { ReplaceLabelingArticleHelper } from '../../label/replace-labeling-helper/replace-labeling-article.helper';
import { UndoDetachArticlesOperation } from '@shared/undo/operation/undo-detach-articles-operation';
import { Injectable } from '@angular/core';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { ArticleAccessoryManager } from '@article/manager/article-accessory.manager';
import { Observable, Subject } from 'rxjs';
import { Log } from '@shared/debug/log';
import { ProjectArticleService } from '@project/service/project-article.service';
import { CopyPasteAdditionalOperations } from './copy-paste-additional-operations';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { BuildingBlockInfo } from './building-block-info';
import { ArticleContainerService } from '@3d-scene/service/article-container.service';
import { Busbar } from '@article/helper/replace/busbar/replace-busbar-data';
import { ReplaceBusbarArticleHelper } from '@article/helper/replace/busbar/replace-busbar-article.helper';
import { ReplaceBusbarService } from '@article/helper/replace/busbar/replace-busbar.service';
import { MessageEntry } from '@shared/alert/message-entry';

@Injectable({ providedIn: 'root' })
export class PasteArticleManager {

  protected _pasteOperationInProgress: boolean = false;
  protected _moveArticlesMap: Dictionary<string, Vector3> = null;
  protected _pasteOperationErrors: MessageEntry[] = [];
  protected _allPasteOperationsSuccessfullyFinished: boolean = true;
  protected _pasteArticleInfos: PasteArticleInformation[] = [];
  protected _processedPasteArticleInfo: PasteArticleInformation;
  protected _rangeOnLineInfo: CopiedRangeOnLineArticleInformation;
  protected _rangeOnLineContainer: ArticleContainer;
  protected _createdByOneArticleInfoUsageIds: string[] = [];
  private _pasteOperationCompleted$ = new Subject<boolean>();

  constructor(
    private userModel: UserModel,
    protected projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    private selectionModel: SelectionModel,
    private articleManager: ArticleManager,
    private clipboardManager: ClipboardManager,
    private pasteArticleProcessor: PasteArticleProcessor,
    private undoRedoHelper: UndoRedoHelper,
    private carrierBlockManager: CarrierBlockManager,
    private localeResourceManager: LocaleResourceManager,
    private toastrService: ToastrService,
    private replaceLabelingHelper: ReplaceLabelingArticleHelper,
    private undoRedoUtils: UndoRedoUtils,
    protected containerService: ContainerService,
    protected articleContainerUtils: ArticleContainerUtils,
    private replaceLabelingUtils: ReplaceLabelingUtils,
    protected insertArticleHelperFactory: InsertArticleHelperFactory,
    private insertArticleUtils: InsertArticleUtils,
    private articleAccessoryManager: ArticleAccessoryManager,
    private copyPasteAdditionalOperations: CopyPasteAdditionalOperations,
    private applicationRefreshService: ApplicationRefreshService,
    protected buildingBlockInfo: BuildingBlockInfo,
    private articleContainerService: ArticleContainerService,
    private replaceBusbarHelper: ReplaceBusbarArticleHelper,
    private replaceBusbarService: ReplaceBusbarService
  ) {}

  public get pasteOperationCompleted$(): Observable<boolean> {
    return this._pasteOperationCompleted$.asObservable();
  }

  public getRangeOnLineInfo(): CopiedRangeOnLineArticleInformation | null {
    // currently only articles connected to the same mounting rail/n-busbar can be copied,
    // so only single articles-info about copied articles is stored
    return this.clipboardManager.getRangeOnLineInfoFromClipboard();
  }

  public getCopiedRangeOnLineArticleType(): string | null {
    const copiedInfo: CopiedRangeOnLineArticleInformation = this.getRangeOnLineInfo();
    return copiedInfo ? copiedInfo.rangeOnLineArticleType : null;
  }

  public getCopiedOverlapInfo(): OverlapInfo | null {
    const copiedInfo: CopiedRangeOnLineArticleInformation = this.getRangeOnLineInfo();
    return copiedInfo ? copiedInfo.overlapInfo : null;
  }

  public clearPasteOperationParameters(): void {
    this._allPasteOperationsSuccessfullyFinished = true;
    this._pasteArticleInfos = [];
    this._processedPasteArticleInfo = null;

    this._pasteOperationInProgress = false;
    this.copyPasteAdditionalOperations.clear();
    this.buildingBlockInfo.createdArticleUsageIds = [];
    this._pasteOperationErrors = [];
    this._moveArticlesMap = null;
    this.pasteArticleProcessor.clear();
  }

  public async finalizePasteOperation(): Promise<void> {
    await this.pushUndoReplaceOperation(this.buildingBlockInfo.createdArticleUsageIds, this._moveArticlesMap);
    await this.applicationRefreshService.refreshProject();
    this.clearPasteOperationParameters();
  }

  private isPasteByRequirementPropertiesAvailable(
    rangeOnLineInfo: CopiedRangeOnLineArticleInformation,
    rangeOnLineContainer: ArticleContainer
  ): boolean {
    if (!rangeOnLineInfo || !rangeOnLineContainer || rangeOnLineInfo.rangeOnLineArticleType !== rangeOnLineContainer.article.type.displayName) {
      return false;
    }
    const acceptingAreas: ConnectionAreaVO[] = ArticleUtils.getConnectionElements(rangeOnLineContainer.article,
      ConnectionAreaVO.SIDE_ACCEPTING);
    const acceptingArea: ConnectionAreaVO = ConnectionInfoUtils.findDefaultRangeOnLineConnection(acceptingAreas);
    if (!acceptingArea) {
      return false;
    }
    for (const articleInformation of rangeOnLineInfo.getArticlesOnRailInformation()) {
      const article: ArticleVO = this.articleManager.getCachedArticle(articleInformation.articleUsage.articleId,
        articleInformation.articleUsage.articleVersion);
      if (!article) {
        return false;
      }
      if (!this.isCheckConnectionPropertiesRequired(article)) {
        continue;
      }
      const requiredConnection: ConnectionAreaVO = ConnectionInfoUtils.findRequiredConnection(
        article, rangeOnLineContainer.article, acceptingArea);
      if (!requiredConnection) {
        this.noRequiredConnectionFound({article: rangeOnLineContainer.article}, {article: article, usage: articleInformation.articleUsage});
        return false;
      }
    }
    return true;
  }

  protected noRequiredConnectionFound(acceptingArticle: { article: ArticleVO, usage?: ArticleUsageVO }, requiringArticle: { article: ArticleVO, usage: ArticleUsageVO }) {
    // Nothing to do here, this method is overridden in the child class
  }

  private isCheckConnectionPropertiesRequired(article: ArticleVO): boolean {
    return !(ArticleUtils.isCustomArticle(article) || ArticleUtils.isZoneArticle(article));
  }

  public isPasteOperationAvailable(): boolean {
    if (!ProjectUtils.isProjectEditable(this.projectModel.currentProject, this.userModel.user) ||
      ProjectUtils.isEmptyProject(this.projectModel.currentProject)) {
      return false;
    }
    const copiedInfo: CopiedRangeOnLineArticleInformation = this.getRangeOnLineInfo();
    if (!copiedInfo ||
      !this.isAvailableProjectTypeToPaste(copiedInfo.projectType) ||
      this.projectArticleService.findArticleUsagesByType(copiedInfo.rangeOnLineArticleType).length === 0
    ) {
      return false;
    }

    if(this.cannotAddArticleToFreemounting(copiedInfo)) {
      return false;
    }

    const selectedUsageIds: string[] = this.selectionModel.getArticleUsageIds();
    if (selectedUsageIds.length === 0) {
      return true;
    }
    if (this.isPasteOperationAvailableForSelectedUsageIds(selectedUsageIds, copiedInfo)) {
      return true;
    }

    return false;
  }

  private isPasteOperationAvailableForSelectedUsageIds(selectedUsageIds: string[], copiedInfo: CopiedRangeOnLineArticleInformation) {
    for (const usageId of selectedUsageIds) {
      const baseContainer: ArticleContainer = this.articleContainerService.getBaseRangeOnLineContainer(
        this.containerService.findArticleContainerInGlobal(usageId));
      if (!baseContainer) {
        continue;
      }
      if (baseContainer.article.type && copiedInfo.rangeOnLineArticleType === baseContainer.article.type.displayName) {
        return true;
      }
    }
    return false;
  }

  private cannotAddArticleToFreemounting(copiedInfo: CopiedRangeOnLineArticleInformation) {
    if (this.projectModel.currentProjectType == "FREE_MOUNTING") {
      for (const copiedArticleInfo of copiedInfo.articlesOnRailInformation) {
        const articleVO: ArticleVO = this.articleManager.getCachedArticle(
          copiedArticleInfo.articleUsage.articleId,
          copiedArticleInfo.articleUsage.articleVersion
        );
        if (!ArticleUtils.canAddArticleToFreeMounting(this.projectModel.currentProject, this.userModel.user, articleVO)) {
          return true;
        }
      }
    }
    return false;
  }

  private isAvailableProjectTypeToPaste(projectType: ProjectType): boolean {
    if (!projectType) {
      return false;
    }

    const currentProjectType: ProjectType = this.projectModel.currentProjectType;
    return (projectType === currentProjectType || (this.isProjectTypeFromCopyGroup(projectType) && this.isProjectTypeFromCopyGroup(currentProjectType)));
  }

  private isProjectTypeFromCopyGroup(projectType: ProjectType): boolean {
    // TODO: (from flex) Bug 2932, temporary solution to support CIRCUIT_BOARD projects
    return "FREE_MOUNTING" === projectType || "MOUNTING_RAIL" === projectType || "CIRCUIT_BOARD" === projectType;
  }

  public async pasteArticlesByPasteArticleInfo(pasteArticleInfos: PasteArticleInformation[]): Promise<void> {
    this._pasteOperationInProgress = true;
    this._pasteArticleInfos = pasteArticleInfos;
    await this.loadArticlesToPaste();
  }

  private async loadArticlesToPaste(): Promise<void> {
    const rangeOnLineInfo: CopiedRangeOnLineArticleInformation = this.getRangeOnLineInfo();
    const articleHeadsToLoad: ArticleVO[] = [];
    const processedArticleIdToVersions: Dictionary<number, number[]> = new Dictionary<number, number[]>();
    let articleUsage: ArticleUsageVO;
    let articleHeadToLoad: ArticleVO;
    for (const articleInformation of rangeOnLineInfo.articlesInformation) {
      articleUsage = articleInformation.articleUsage;
      articleHeadToLoad = this.getArticleHeadForLoadingArticle(articleUsage.articleId, articleUsage.articleVersion, processedArticleIdToVersions);
      if (articleHeadToLoad) {
        articleHeadsToLoad.push(articleHeadToLoad);
      }
      if (ArticleUsageUtils.isCompositePartArticleUsage(articleUsage)) {
        const compositionIdentity: number = PropertyUtils.getNumberPropertyValue(
          articleUsage.infoProperties,
          ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_ID
        );
        const compositionVersion: number = PropertyUtils.getIntPropertyValue(
          articleUsage.infoProperties,
          ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_VERSION
        );
        articleHeadToLoad = this.getArticleHeadForLoadingArticle(compositionIdentity, compositionVersion, processedArticleIdToVersions);
        if (articleHeadToLoad) {
          articleHeadsToLoad.push(articleHeadToLoad);
        }
      }
    }
    if (articleHeadsToLoad.length > 0) {
      this.articleAccessoryManager.getArticlesByHeads(articleHeadsToLoad);
      await this.loadArticlesByHeads(articleHeadsToLoad);
    } else {
      await this.getArticlesByHeadsSuccess();
    }
  }

  protected async loadArticlesByHeads(articleHeadsToLoad: ArticleVO[]): Promise<void> {

    try {
      await this.articleManager.loadArticlesByHeads(articleHeadsToLoad);
      await this.getArticlesByHeadsSuccess();
    } catch (error) {
      Log.error('Could not load articles by head: ', error);
      await this.getFullArticlesFailure();
    }
  }

  private getArticleHeadForLoadingArticle(
    articleId: number,
    articleVersion: number,
    processedArticleIdToVersions: Dictionary<number, number[]>
  ): ArticleVO | null {
    const article: ArticleVO = this.articleManager.getCachedArticle(articleId, articleVersion);
    if (!article && this.checkAndUpdateProcessedArticleData(articleId, articleVersion, processedArticleIdToVersions)) {
      const articleHead: ArticleVO = new ArticleVO();
      articleHead.identity = articleId;
      articleHead.version = articleVersion;
      return articleHead;
    }
    return null;
  }

  /**
   * Adds article-version in <code>processedArticleIdToVersions</code> map by the given article-id if it doesn't already exist in the map.
   *
   * @param articleId article identity
   * @param articleVersion article version
   * @param processedArticleIdToVersions already processed article information (key: article identity, value: array of article-versions)
   * @return true if article version was added to the map, otherwise false
   *
   */
  private checkAndUpdateProcessedArticleData(
    articleId: number,
    articleVersion: number,
    processedArticleIdToVersions: Dictionary<number, number[]>
  ): boolean {
    let articleDataUpdated: boolean = false;
    let processedVersions: number[] = processedArticleIdToVersions.getValue(articleId);
    if (!processedVersions) {
      processedVersions = [];
      processedArticleIdToVersions.setValue(articleId, processedVersions);
    }
    if (processedVersions.indexOf(articleVersion) === -1) {
      processedVersions.push(articleVersion);
      articleDataUpdated = true;
    }
    return articleDataUpdated;
  }

  public async getArticlesByHeadsSuccess(): Promise<void> {
    if (this._pasteOperationInProgress) {
      await this.processNextPasteOperation();
    }
  }

  private async continuePasteOperation(): Promise<void> {
    this._rangeOnLineInfo = this.getRangeOnLineInfo();
    this._rangeOnLineContainer = this.containerService.findArticleContainerInGlobal(this._processedPasteArticleInfo.rangeOnLineUsageId);
    this.filterCopiedRangeOnLineInfo(this._rangeOnLineInfo);
    if (await this.isPasteAvailable()) {
      await this.executePasteOperation();
      this.displayToasterMessage(this.localeResourceManager.getValueByKey('toast.message.article_pasted'));
      return;
    }
    await this.processNextPasteOperation();
  }

  private async isPasteAvailable(): Promise<boolean> {
    if (!this.isPasteByRequirementPropertiesAvailable(this._rangeOnLineInfo, this._rangeOnLineContainer)) {
      this.processPasteOperationError('copyArticleManager.paste.message.range_on_line_connection_not_found', 'copyArticleManager.paste.title.error');
      return false;
    }

    this.pasteArticleProcessor.clear();
    const insertArticleHelper: InsertArticleHelper = this.insertArticleHelperFactory.createInstanceByRangeOnLineArticleContainer(
      this._rangeOnLineContainer
    );
    const commonLength: number = this._rangeOnLineInfo.commonArticlesLength;
    const commonLine: AdvancedLine = insertArticleHelper.calculateArticleLine(this._processedPasteArticleInfo.pasteOffset, commonLength);
    commonLine.overlapInfo = this._rangeOnLineInfo.overlapInfo;
    const busbarContainers: ArticleContainer[] = this.replaceBusbarService.getBusbarsToRecreateByInsertionPoint(
      commonLine.start,
      insertArticleHelper);
    const moveArticlesMap: Dictionary<string, Vector3> = insertArticleHelper.calculateMoveOffsetByArticleLine(commonLine, busbarContainers.map((busbar) => busbar.articleGUID));
    const labelingContainers: ArticleContainer[] = this.replaceLabelingUtils.getLabelStripsToRecreateByMoveMap(
      commonLine.start,
      moveArticlesMap,
      insertArticleHelper
    );
    if (moveArticlesMap && !CompositeArticleUtils.isCompositePartArticleExists(labelingContainers) && !CompositeArticleUtils.isCompositePartArticleExists(busbarContainers)) {
      if (ArrayUtils.isNotEmpty(labelingContainers)) {
        // if labeling containers were moved during previous operations should move them to original position before detaching to correctly perform undo
        this.moveDynamicArticlesToOriginalPositionIfParentWasMoved(labelingContainers);
        const detachOperation: UndoDetachArticlesOperation = await this.replaceLabelingHelper.detachLabelStrips(labelingContainers);
        if (detachOperation) {
          this.copyPasteAdditionalOperations.push(detachOperation);
        }
      }
      if (ArrayUtils.isNotEmpty(busbarContainers)) {
        // build busbar-info before busbar container moved to its original position
        // otherwise Busbar.endOverhangChildrenUsageIds will not be correctly found
        const busbars: Busbar[] = this.replaceBusbarHelper.mapBusbarContainers(busbarContainers);
        // if busbar containers were moved during previous operations should move them to original position before detaching to correctly perform undo
        this.moveDynamicArticlesToOriginalPositionIfParentWasMoved(busbarContainers);
        await this.replaceBusbarHelper.detachBusbars(busbars);
      }
      return true;
    } else {
      this.processPasteOperationError('copyArticleManager.paste.message.error_free_space');
    }
    return false;
  }

  private moveDynamicArticlesToOriginalPositionIfParentWasMoved(dynamicContainers: ArticleContainer[]): void {
    if (ArrayUtils.isEmpty(dynamicContainers) || !this._moveArticlesMap) {
      return;
    }
    for (const dynamicContainer of dynamicContainers) {
      const parentArticlesGUIDs: string[] = this.articleContainerUtils.getAllBackwardArticleContainerOnRailRecursively(dynamicContainer)
        .map((a) => a.articleGUID);
      const allParentArticlesWasMoved: boolean =
        ArrayUtils.isNotEmpty(parentArticlesGUIDs) &&
        ArrayUtils.sourceArrayContainsAllElementsFromTargetArray(this._moveArticlesMap.keys(), parentArticlesGUIDs);
      if (allParentArticlesWasMoved) {
        const offsets: Vector3[] = [];
        parentArticlesGUIDs.forEach((guid: string) => {
          offsets.push(this._moveArticlesMap.getValue(guid));
        });
        const isOffsetsEquals: boolean = offsets.every((offset) => MathUtils.equalsNumber3D(offset, offsets[0]));
        if (isOffsetsEquals) {
          const targetOffset: Vector3 = offsets[0];
          this.insertArticleUtils.moveSingleArticleByOffset(dynamicContainer, targetOffset, true);
          const connectedContainers: ArticleContainer[] = this.articleContainerUtils.getConnectedArticlesRecursively(dynamicContainer);
          connectedContainers.forEach((connectedContainer) => {
            InsertArticleUtils.addToMoveMap(this._moveArticlesMap, connectedContainer.articleGUID, targetOffset);
          });
        }
      }
    }
  }

  private async executePasteOperation(): Promise<void> {
    this.pasteArticleProcessor.clear();
    try {
      const createdContainerGuids = await this.pasteArticleProcessor.pasteArticlesByRangeOnLineInfo(this._rangeOnLineInfo, this._rangeOnLineContainer,
        this._processedPasteArticleInfo.pasteOffset);
      await this.pasteArticlesSuccess(createdContainerGuids);
    } catch (error) {
      await this.pasteArticlesFailure(error.message);
    }
  }

  private filterCopiedRangeOnLineInfo(rangeOnLineInfo: CopiedRangeOnLineArticleInformation): void {
    const articlesOnRailInformation: CopiedArticleInformation[] = rangeOnLineInfo.getArticlesOnRailInformation();
    const articlesInformation: CopiedArticleInformation[] = rangeOnLineInfo.getArticlesInformation();
    for (const articleInformation of articlesOnRailInformation.concat()) {
      const article: ArticleVO = this.articleManager.getCachedArticle(articleInformation.articleUsage.articleId,
        articleInformation.articleUsage.articleVersion);
      if (!article) {
        return;
      }
      if (ArticleUtils.isZoneArticle(article)) {
        if (!IsProjectType.visibleMountingRail(this.projectModel.currentProjectType)) {
          this.processPasteOperationError('copyArticleManager.paste.message.insert_zone_article_not_supported');
          articlesOnRailInformation.splice(articlesOnRailInformation.indexOf(articleInformation), 1);
          articlesInformation.splice(articlesInformation.indexOf(articleInformation), 1);
        } else {
          ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(articleInformation.articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_DEPTH, NaN);
        }
      }
    }
  }

  public async pasteArticlesSuccess(createdContainerGUIDs: string[]): Promise<void> {
    if (!this._pasteOperationInProgress) {
      return;
    }
    this.buildingBlockInfo.createdArticleUsageIds = this.buildingBlockInfo.createdArticleUsageIds.concat(createdContainerGUIDs);
    this._createdByOneArticleInfoUsageIds = this._createdByOneArticleInfoUsageIds.concat(createdContainerGUIDs);
    this.copyPasteAdditionalOperations.pushMany(this.pasteArticleProcessor.additionalArticlesOperations);
    this.mergeMoveArticlesMap(this.pasteArticleProcessor.moveMap);
    this.carrierBlockManager.includeArticlesInNeighborBlock(createdContainerGUIDs);
    if (this._processedPasteArticleInfo.count > 1) {
      this._processedPasteArticleInfo.count--;
      this._processedPasteArticleInfo.pasteOffset += this._rangeOnLineInfo.commonArticlesLength;
      await this.executePasteOperation();
    } else {
      this._createdByOneArticleInfoUsageIds = [];
      await this.processNextPasteOperation();
    }
  }

  public async pasteArticlesFailure(errorMessage: string): Promise<void> {
    if (!this._pasteOperationInProgress) {
      return;
    }
    this.processPasteOperationError(errorMessage);
    await this.processNextPasteOperation();
  }

  private async processNextPasteOperation(): Promise<void> {
    if (ArrayUtils.isEmpty(this._pasteArticleInfos)) {
      await this.finalizePasteOperations();
    } else {
      this._processedPasteArticleInfo = this._pasteArticleInfos.shift();
      this._processedPasteArticleInfo.recalculatePasteOffset();
      await this.continuePasteOperation();
    }
  }

  public async getFullArticlesFailure(): Promise<void> {
    if (this._pasteOperationInProgress) {
      this.processPasteOperationError('copyArticleManager.paste.message.copied_articles_can_not_loaded',
        'copyArticleManager.paste.title.error');
      await this.finalizePasteOperations();
    }
  }

  protected async finalizePasteOperations(): Promise<void> {
    this._pasteOperationCompleted$.next(this._allPasteOperationsSuccessfullyFinished)
  }

  private processPasteOperationError(errorMessage: string, errorTitle: string = 'copyArticleManager.paste.title.error'): void {
    this._allPasteOperationsSuccessfullyFinished = false;
    this.storePasteOperationError(errorMessage, errorTitle);
  }

  private storePasteOperationError(errorMessage: string, errorTitle: string = 'copyArticleManager.paste.title.error'): void {
    this._pasteOperationErrors.unshift({title: errorTitle, message: errorMessage, type: "error"});
  }

  public getFirstPasteError(): MessageEntry | null {
    return this._pasteOperationErrors.length > 0 ? this._pasteOperationErrors[0] : null;
  }

  private async pushUndoReplaceOperation(createdContainerGUIDs: string[], moveMap: Dictionary<string, Vector3>): Promise<void> {
    const attachOperation: UndoAttachArticlesOperation = this.undoRedoUtils.createUndoAttachOperationByUsageIds(createdContainerGUIDs);
    if (attachOperation) {
      const undoInsertOperation: UndoInsertArticlesByLocationOperation = new UndoInsertArticlesByLocationOperation(
        attachOperation,
        moveMap,
        this.insertArticleUtils
      );
      undoInsertOperation.additionalOperations = this.copyPasteAdditionalOperations.operations;
      await this.undoRedoHelper.pushUndoOperation(undoInsertOperation);
    }
  }

  private mergeMoveArticlesMap(moveMap: Dictionary<string, Vector3>): void {
    if (!moveMap) {
      return;
    }
    if (!this._moveArticlesMap) {
      this._moveArticlesMap = new Dictionary<string, Vector3>();
    }
    InsertArticleUtils.mergeMoveMap(this._moveArticlesMap, moveMap);
  }

  public clear(): void {
    this.copyPasteAdditionalOperations.clear();
  }

  protected displayToasterMessage(title: string, body: string = ''): void {
    this.toastrService.show(body, title);
  }
}
